// ฤดูกาลและการอพยพ (docs/PLAN.md ข้อ 4.3 เฟส 3)
// เวลาในเกมนับเป็นเดือนจากเดือนเกิด สัตว์อพยพเกิดที่แหล่งผสมพันธุ์ตอนต้นฤดู แล้วย้ายไปมาตามฤดูระหว่างสองแหล่ง
// สัตว์อายุยืนที่หนึ่ง turn ยาวหลายเดือน (เช่น วาฬ) นับแต่ละ turn เป็นการเดินทางรอบใหม่ แทนการดูเดือน

import { inBoxes, migrantOf, type Flyway, type Migrant, type MigrationRoute, type Waypoint } from "../data/migrants";
import { haversineKm, type LatLon } from "../engine/grid";
import type { SpeciesInfo, StageTag } from "../engine/types";

export type Ground = "a" | "b";

/** ข้อมูลการอพยพที่เก็บในสถานะชีวิต */
export interface MigrationState {
  route: number; // เส้นทางของประชากรที่เราเกิด (ลำดับใน Migrant.routes)
  month0: number; // เดือนที่เกิด 1–12
  heading?: Ground; // กำลังอพยพไปแหล่งไหน (ผลของตัวเลือกล่าสุด หน้าเกมใช้ย้ายตำแหน่ง)
}

export const MONTH_TH = ["มกราคม", "กุมภาพันธ์", "มีนาคม", "เมษายน", "พฤษภาคม", "มิถุนายน", "กรกฎาคม", "สิงหาคม", "กันยายน", "ตุลาคม", "พฤศจิกายน", "ธันวาคม"];
const DAYS_PER_MONTH = 30.44;
export const LONG_TURN_DAYS = 120;

export function inSeasonA(route: MigrationRoute, month: number): boolean {
  const [s, e] = route.aMonths;
  return s <= e ? month >= s && month <= e : month >= s || month <= e;
}

export const seasonGround = (route: MigrationRoute, month: number): Ground => (inSeasonA(route, month) ? "a" : "b");

export function groundAt(route: MigrationRoute, at: LatLon): Ground | null {
  if (inBoxes(route.a, at.lat, at.lon)) return "a";
  if (inBoxes(route.b, at.lat, at.lon)) return "b";
  return null;
}

/** เส้นทางของประชากรที่อยู่ตรงนี้ (-1 = ประชากรแถวนี้ไม่อพยพ เช่น เหยี่ยวออสเปรในออสเตรเลีย) */
export function routeAt(migrant: Migrant, at: LatLon): number {
  const a = migrant.routes.findIndex((r) => inBoxes(r.a, at.lat, at.lon));
  return a >= 0 ? a : migrant.routes.findIndex((r) => inBoxes(r.b, at.lat, at.lon));
}

/**
 * ตอนเกิด: เกิดตอนต้นฤดูที่แหล่งผสมพันธุ์ (ถ้าเกิดที่แหล่งอื่นก็นับเดือนแรกนอกฤดูผสมพันธุ์)
 * สัตว์อายุสั้นไม่ถึงปี (ผีเสื้อโมนาร์ช) เกิดปลายฤดู เป็นรุ่นที่ได้อพยพ
 */
export function migrationAtBirth(species: Pick<SpeciesInfo, "sci" | "traits">, at: LatLon): MigrationState | undefined {
  const migrant = migrantOf(species.sci);
  if (!migrant) return undefined;
  const route = routeAt(migrant, at);
  if (route < 0) return undefined;
  const r = migrant.routes[route];
  if (migrant.cycle) return { route, month0: r.aMonths[0] };
  if (groundAt(r, at) === "b") {
    // สัตว์อายุสั้น (ผีเสื้อ) ที่เกิดที่แหล่ง b เป็นรุ่นที่จะบินกลับไปแหล่ง a จึงเกิดก่อนฤดูของแหล่ง a เล็กน้อย
    if (species.traits.lifespanYears < 1) return { route, month0: ((r.aMonths[0] + 10) % 12) + 1 };
    return { route, month0: (r.aMonths[1] % 12) + 1 };
  }
  return { route, month0: species.traits.lifespanYears < 1 ? r.aMonths[1] : r.aMonths[0] };
}

export const monthAt = (m: MigrationState, ageDays: number) => ((m.month0 - 1 + Math.floor(ageDays / DAYS_PER_MONTH)) % 12) + 1;

export interface MigrationNow {
  migrant: Migrant;
  route: MigrationRoute;
  month: number;
  here: Ground | null;
  target: Ground; // ฤดูนี้ควรอยู่แหล่งไหน
  due: boolean; // ถึงเวลาออกเดินทาง
  long: boolean; // หนึ่ง turn ยาวกว่าฤดู
}

/** ช่วงชีวิตของตัวเรา ใช้กับสัตว์ที่อพยพตามช่วงวัยแทนตามฤดู (ปลาแซลมอน) */
export interface LifePhase {
  tag: StageTag;
  turnInStage: number;
}

// ปลาแซลมอนโตเต็มวัยอยู่ในทะเลกี่ turn ก่อนกลับไปวางไข่ (ราว 2–4 ปีตามอายุขัย)
export const SEA_TURNS = 6;

/** แหล่งที่ปลาแซลมอนควรอยู่ตอนนี้: ยังเล็กอยู่แม่น้ำ โตเต็มวัยลงทะเล อยู่ทะเลครบแล้วกลับไปวางไข่ */
export function anadromousGround(phase: LifePhase): Ground {
  if (phase.tag !== "adult" && phase.tag !== "old") return "a";
  return phase.tag === "adult" && phase.turnInStage < SEA_TURNS ? "b" : "a";
}

// เต่าทะเลโตเต็มวัยกลับไปวางไข่ทุกกี่ turn (ตัวจริงทุก 2–4 ปี)
export const NEST_EVERY = 3;

/** แหล่งที่เต่าทะเลควรอยู่ตอนนี้: ไข่อยู่บนหาด ฟักแล้วออกทะเล โตแล้วกลับมาวางไข่ที่หาดเดิมทุก NEST_EVERY turn */
export function natalBeachGround(phase: LifePhase): Ground {
  if (phase.tag === "dormant") return "a";
  if (phase.tag !== "adult") return "b";
  return phase.turnInStage % NEST_EVERY === NEST_EVERY - 1 ? "a" : "b";
}

export function migrationNow(sci: string, m: MigrationState, at: LatLon, ageDays: number, daysPerTurn: number, phase?: LifePhase): MigrationNow | null {
  const migrant = migrantOf(sci);
  const route = migrant?.routes[m.route];
  if (!migrant || !route) return null;
  const month = monthAt(m, ageDays);
  const here = groundAt(route, at);
  if (migrant.cycle) {
    // อพยพตามช่วงวัย ไม่ใช่ตามฤดู จึงไม่สนว่า turn ยาวแค่ไหน
    const target = !phase ? "a" : migrant.cycle === "anadromous" ? anadromousGround(phase) : natalBeachGround(phase);
    return { migrant, route, month, here, target, due: here !== target, long: false };
  }
  if (daysPerTurn >= LONG_TURN_DAYS) {
    // หนึ่ง turn ยาวกว่าฤดู: ทุก turn ถึงเวลาเดินทางไปอีกแหล่งหนึ่ง
    const target: Ground = here === "a" ? "b" : here === "b" ? "a" : seasonGround(route, month);
    return { migrant, route, month, here, target, due: true, long: true };
  }
  const target = seasonGround(route, month);
  return { migrant, route, month, here, target, due: here !== target, long: false };
}

export const groundName = (route: MigrationRoute, g: Ground) => (g === "a" ? route.aNameTh : route.bNameTh);
export const groundKind = (migrant: Migrant, g: Ground) => (g === "a" ? migrant.aKindTh ?? "แหล่งผสมพันธุ์" : migrant.bKindTh ?? "แหล่งหากินนอกฤดูผสมพันธุ์");

const pathKm = (points: readonly LatLon[]) => points.slice(1).reduce((km, p, i) => km + haversineKm(points[i], p), 0);
const toLatLon = ([lat, lon]: Waypoint): LatLon => ({ lat, lon });

/**
 * จุดที่ผ่านระหว่างอพยพจาก from ไปแหล่ง heading ตามเส้นทางจริงของประชากรนี้ (ไม่รวมต้นทางและปลายทาง)
 * ถ้ามีหลายสาย (เช่น นกกระสาขาวข้ามช่องแคบยิบรอลตาร์หรือบอสพอรัส) ใช้สายที่ทำให้เดินทางสั้นที่สุด
 */
export function flywayFor(route: MigrationRoute, heading: Ground, from: LatLon, to: LatLon): Flyway {
  const options = heading === "b" ? route.via : route.viaBack ?? route.via?.map((f) => [...f].reverse());
  let best: Flyway = [];
  let bestKm = Infinity;
  for (const f of options ?? []) {
    const km = pathKm([from, ...f.map(toLatLon), to]);
    if (km < bestKm) [best, bestKm] = [f, km];
  }
  return best;
}

/** ชื่อจุดที่ผ่านแบบอ่านง่าย เช่น "ช่องแคบยิบรอลตาร์ ทะเลทรายสะฮารา และทุ่งซาเฮล" */
export function flywayNames(flyway: Flyway): string {
  const names = [...new Set(flyway.map((w) => w[2]).filter((n): n is string => !!n))];
  return names.length <= 1 ? names.join("") : `${names.slice(0, -1).join(" ")} และ${names.at(-1)}`;
}

const boxCenter = ([s, w, n, e]: readonly number[]): LatLon => ({ lat: (s + n) / 2, lon: (w + e) / 2 });

/** เส้นทางอพยพทุกสายสำหรับวาดบนแผนที่ จากกลางแหล่ง a ผ่านจุดแวะไปกลางแหล่ง b (ขากลับที่ต่างทางวาดด้วย) */
export function flywayLines(route: MigrationRoute): LatLon[][] {
  const a = boxCenter(route.a[0]);
  const b = boxCenter(route.b[0]);
  const there = (route.via ?? []).map((f) => [a, ...f.map(toLatLon), b]);
  const back = (route.viaBack ?? []).map((f) => [b, ...f.map(toLatLon), a]);
  return [...there, ...back];
}
